/*
 * <rd-product-row> — product rows filled in the browser (see snippets/rd-product-row.liquid).
 *
 * data-mode="collection": best sellers of a collection (top 48), sold-out dropped,
 *                         shuffled, first N shown. Loads when scrolled near.
 * data-mode="recent":     products from this browser's recently-viewed list
 *                         (written by snippets/rd-recently-viewed-tracker.liquid).
 * data-mode="recommend":  Shopify recommendations for the last viewed product,
 *                         minus anything already in the recently-viewed list.
 *
 * Cards come from sections/rd-product-cards.liquid via the Section Rendering API,
 * so they are Dawn's own card-product markup.
 */
(() => {
  if (customElements.get('rd-product-row')) return;

  const RECENT_KEY = 'rd:recently-viewed';
  const CARDS_SECTION = 'rd-product-cards';

  const readRecent = () => {
    try {
      const list = JSON.parse(localStorage.getItem(RECENT_KEY));
      return Array.isArray(list) ? list.filter((item) => item && item.handle) : [];
    } catch (e) {
      return [];
    }
  };

  const shuffle = (items) => {
    for (let i = items.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [items[i], items[j]] = [items[j], items[i]];
    }
    return items;
  };

  const fetchCards = async (url) => {
    const response = await fetch(url);
    if (!response.ok) throw new Error(`${response.status} ${url}`);
    const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
    return Array.from(doc.querySelectorAll('[data-rd-cards] > li'));
  };

  const withSection = (url, params = {}) => {
    const u = new URL(url, window.location.origin);
    Object.entries({ ...params, section_id: CARDS_SECTION }).forEach(([k, v]) => u.searchParams.set(k, v));
    return u.toString();
  };

  const isAvailable = (li) => li.dataset.available === 'true';

  class RdProductRow extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.limit = parseInt(this.dataset.limit, 10) || 8;
      this.root = (this.dataset.root || '/').replace(/\/?$/, '/');

      if (this.dataset.mode === 'collection' && 'IntersectionObserver' in window) {
        const observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            observer.disconnect();
            this.load();
          },
          { rootMargin: '600px 0px' }
        );
        observer.observe(this);
      } else {
        this.load();
      }
    }

    async load() {
      let items = [];
      try {
        if (this.dataset.mode === 'collection') items = await this.loadCollection();
        if (this.dataset.mode === 'recent') items = await this.loadRecent();
        if (this.dataset.mode === 'recommend') items = await this.loadRecommend();
      } catch (e) {
        console.error('[rd-product-row]', e);
      }
      this.render(items.slice(0, this.limit));
    }

    async loadCollection() {
      if (!this.dataset.url) return [];
      const cards = await fetchCards(withSection(this.dataset.url, { sort_by: 'best-selling' }));
      return shuffle(cards.filter(isAvailable));
    }

    async loadRecent() {
      const handles = readRecent()
        .slice(0, this.limit)
        .map((item) => item.handle);
      const results = await Promise.allSettled(
        handles.map((handle) => fetchCards(withSection(`${this.root}products/${encodeURIComponent(handle)}`)))
      );
      return results.filter((r) => r.status === 'fulfilled' && r.value[0]).map((r) => r.value[0]);
    }

    async loadRecommend() {
      const recent = readRecent();
      if (!recent.length || !recent[0].id) return [];
      const seen = new Set(recent.map((item) => item.handle));
      const cards = await fetchCards(
        withSection(`${this.root}recommendations/products`, {
          product_id: recent[0].id,
          limit: Math.min(this.limit + recent.length, 10),
          intent: 'related',
        })
      );
      return cards.filter((li) => isAvailable(li) && !seen.has(li.dataset.handle));
    }

    render(items) {
      const list = this.querySelector('[data-rd-list]');
      this.removeAttribute('aria-busy');

      if (!items.length) {
        const note = this.querySelector('[data-rd-note]');
        if (this.hasAttribute('data-design-mode') && note) {
          list.replaceChildren();
          note.hidden = false;
        } else {
          this.hidden = true;
        }
        return;
      }

      items.forEach((li) => this.scopeIds(li));
      list.replaceChildren(...items);
      this.hidden = false;

      if (typeof initializeScrollAnimationTrigger === 'function') initializeScrollAnimationTrigger(list);
      this.setupArrows(list);
    }

    // Desktop arrows: page the row by its visible width; hidden when everything fits.
    setupArrows(list) {
      const arrows = this.querySelector('[data-rd-arrows]');
      if (!arrows) return;
      const prev = arrows.querySelector('[data-rd-prev]');
      const next = arrows.querySelector('[data-rd-next]');

      const update = () => {
        const max = list.scrollWidth - list.clientWidth;
        arrows.hidden = max <= 2;
        prev.disabled = list.scrollLeft <= 2;
        next.disabled = list.scrollLeft >= max - 2;
      };
      const page = (direction) => list.scrollBy({ left: direction * list.clientWidth, behavior: 'smooth' });

      prev.addEventListener('click', () => page(-1));
      next.addEventListener('click', () => page(1));
      list.addEventListener('scroll', update, { passive: true });
      if ('ResizeObserver' in window) new ResizeObserver(update).observe(list);
      update();
    }

    // card-product builds ids from the product id, so the same product in two rows
    // would duplicate ids; prefix them per row and update the references.
    scopeIds(root) {
      const prefix = `${this.id}-`;
      root.querySelectorAll('[id]').forEach((el) => (el.id = prefix + el.id));
      ['aria-labelledby', 'aria-describedby', 'aria-controls', 'for'].forEach((attr) => {
        root.querySelectorAll(`[${attr}]`).forEach((el) => {
          el.setAttribute(
            attr,
            el
              .getAttribute(attr)
              .split(/\s+/)
              .map((id) => (id ? prefix + id : id))
              .join(' ')
          );
        });
      });
    }
  }

  customElements.define('rd-product-row', RdProductRow);
})();
